import { useEffect, useState } from 'react'
import { Menu, ChevronLeft, ChevronDown } from 'lucide-react'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { OrganizationSidebarContent } from '@/components/OrganizationSidebar'
import { useNavigate, useParams } from 'react-router'
import PerformanceTab from './components/PerformanceTab'
import CertificationApprovalTab from './components/CertificationApprovalTab'
import FeedbackTab from './components/FeedbackTab'
import { couplesApi, type CoupleDetail, type CoupleProgress } from '@/utils/api'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'

type TabType = 'details' | 'performance' | 'certification' | 'feedback'
type StatusType = 'Completed' | 'Ongoing' | 'Approval' | 'Pending'

const normalizeStatus = (status?: string | null) =>
  (status ?? '').toLowerCase().replace(/[-\s]/g, '_')

const toUiStatus = (status?: string | null): StatusType => {
  const normalized = normalizeStatus(status)
  if (normalized === 'completed') return 'Completed'
  if (normalized === 'in_progress') return 'Ongoing'
  if (normalized === 'certified') return 'Approval'
  return 'Pending'
}

const formatDate = (value?: string | null) => {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed
    .toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
    .replace(/ /g, '-')
}

const formatValue = (value?: string | null) => (value ? value : '-')

const formatPersonName = (person?: { firstName?: string | null; lastName?: string | null } | null) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(' ').trim()

const resolveProgressPercent = (completed?: boolean | null, ...values: Array<number | null | undefined>) => {
  if (completed) return 100
  const raw = values.find((value) => typeof value === 'number' && !Number.isNaN(value))
  if (typeof raw !== 'number') return 0
  return Math.max(0, Math.min(100, Math.round(raw)))
}

export default function CoupleDetails() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const displayName = getUserDisplayName(user)
  const initials = getUserInitials(user)
  const [activeTab, setActiveTab] = useState<TabType>('details')
  const [couple, setCouple] = useState<CoupleDetail | null>(null)
  const [progress, setProgress] = useState<CoupleProgress | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const tabs = [
    { id: 'details', label: "Couple's Details" },
    { id: 'performance', label: 'Performance' },
    { id: 'certification', label: 'Certification Approval' },
    { id: 'feedback', label: 'Feedback' },
  ]

  useEffect(() => {
    if (!id) {
      setError('Missing couple id.')
      setLoading(false)
      return
    }

    let active = true
    const fetchDetails = async () => {
      try {
        setLoading(true)
        setError(null)
        const [detailResult, progressResult] = await Promise.allSettled([
          couplesApi.detail(id),
          couplesApi.progress(id),
        ])

        if (!active) return

        if (detailResult.status === 'fulfilled') {
          const detailPayload =
            (detailResult.value as { data?: CoupleDetail }).data ||
            (detailResult.value as unknown as CoupleDetail)
          setCouple(detailPayload || null)
        } else {
          console.error('Failed to load couple details:', detailResult.reason)
          setError(
            (detailResult.reason as Error)?.message || 'Failed to load couple details'
          )
        }

        if (progressResult.status === 'fulfilled') {
          const progressPayload =
            (progressResult.value as { data?: CoupleProgress }).data ||
            (progressResult.value as unknown as CoupleProgress)
          setProgress(progressPayload || null)
        } else {
          console.error('Failed to load couple progress:', progressResult.reason)
        }
      } catch (err) {
        if (!active) return
        console.error('Failed to load couple details:', err)
        setError((err as Error)?.message || 'Failed to load couple details')
      } finally {
        if (active) setLoading(false)
      }
    }

    void fetchDetails()

    return () => {
      active = false
    }
  }, [id])

  const personOneName =
    formatPersonName(couple?.personOne) ||
    progress?.personOne?.name ||
    progress?.personOneName ||
    ''
  const personTwoName =
    formatPersonName(couple?.personTwo) ||
    progress?.personTwo?.name ||
    progress?.personTwoName ||
    ''
  const headerName = personOneName || 'Couple Details'
  const statusLabel = toUiStatus(couple?.status || progress?.status)

  const personOneCompleted =
    progress?.personOne?.completed ??
    progress?.isPersonOneCompleted ??
    couple?.isPersonOneCompleted ??
    null
  const personTwoCompleted =
    progress?.personTwo?.completed ??
    progress?.isPersonTwoCompleted ??
    couple?.isPersonTwoCompleted ??
    null

  const personOneProgress = resolveProgressPercent(
    personOneCompleted,
    progress?.progressPercentage,
    couple?.progressPercentage
  )
  const personTwoProgress = resolveProgressPercent(
    personTwoCompleted,
    progress?.progressPercentage,
    couple?.progressPercentage
  )
  const personOneLabel = personOneName || 'Person One'
  const personTwoLabel = personTwoName || 'Person Two'

  const getStatusBadgeColor = (status: StatusType) => {
    const colors: Record<StatusType, string> = {
      Completed: 'bg-[#aee753] text-[#4b6029]',
      Ongoing: 'bg-[#aee753] text-[#4b6029]',
      Approval: 'bg-[#fee2e2] text-[#dc2626]',
      Pending: 'bg-[#ffedd5] text-[#ea580c]',
    }
    return colors[status]
  }

  return (
    <div className='min-h-screen bg-[#edeff3]'>
      {/* Main Content */}
      <div className='p-4 md:p-6 lg:p-8'>
        {/* Single White Container for entire page */}
        <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8'>
          {/* Top Section - User Profile */}
          <div className='flex items-center justify-between mb-6 sm:mb-8'>
            {/* Mobile Menu Button */}
            <Sheet>
              <SheetTrigger asChild>
                <button className='lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors'>
                  <Menu className='h-6 w-6 text-gray-700' />
                </button>
              </SheetTrigger>
              <SheetContent side='left' className='p-0 w-[280px]'>
                <OrganizationSidebarContent />
              </SheetContent>
            </Sheet>

            {/* User Profile (Desktop) */}
            <div className='hidden lg:flex items-center gap-3 ml-auto'>
              <div className='flex items-center gap-3'>
                <div className='h-[47px] w-[48px] rounded-full bg-[#434343] flex items-center justify-center text-white font-bold text-[16px]'>
                  {initials}
                </div>
                <span className='text-[24px] font-bold text-[#353535]'>{displayName}</span>
              </div>
              <button className='w-[41px] h-[39px] bg-[#f7f8fa] rounded-full flex items-center justify-center'>
                <ChevronDown className='h-4 w-4 text-gray-600' />
              </button>
            </div>
          </div>

          {/* Back Button and Title */}
          <div className='flex items-center gap-3 sm:gap-5 mb-4 sm:mb-6'>
            <button
              onClick={() => navigate('/organization/couples')}
              className='w-[32px] h-[32px] bg-[#eaeaea] rounded-full flex items-center justify-center hover:bg-[#d9d9d9] transition-colors shrink-0'
            >
              <ChevronLeft className='h-6 w-6 text-[#151515]' />
            </button>
            <h1 className='text-[22px] sm:text-[28px] md:text-[32px] font-bold text-[#151515] leading-tight truncate'>
              {loading ? 'Loading...' : headerName}
            </h1>
            <span className={`px-3 py-1 rounded-[5px] text-[10px] font-medium shrink-0 ${getStatusBadgeColor(statusLabel)}`}>
              {statusLabel === 'Ongoing' ? 'Ongoing' : statusLabel}
            </span>
          </div>

          {/* Tabs */}
          <div className='flex gap-2 sm:gap-4 overflow-x-auto pb-1 mb-6 sm:mb-8'>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`px-3 sm:px-4 py-2 rounded-[8px] text-[13px] sm:text-[14px] font-bold whitespace-nowrap shrink-0 transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#151515] text-white'
                    : 'bg-white text-[#151515] border border-[#e0e0e0] hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {error && (
            <p className='mb-4 text-[14px] text-red-600'>{error}</p>
          )}

          {/* Tab Content - Inside same white container */}
          {activeTab === 'details' && (
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            {/* Man Section */}
            <div className='flex flex-col gap-4'>
              <h2 className='text-[18px] font-bold text-[#151515] leading-[28px]'>Man</h2>
              <div className='bg-white border border-[#e8e8e8] rounded-[10px] p-5'>
                <div className='space-y-[18px]'>
                  <div className='flex items-center gap-[6px] text-[14px] text-[#202020]'>
                    <span className='font-normal'>First Name:</span>
                    <span className='font-bold'>{formatValue(couple?.personOne?.firstName)}</span>
                  </div>
                  <div className='flex items-center gap-[6px] text-[14px] text-[#202020]'>
                    <span className='font-normal'>Last Name:</span>
                    <span className='font-bold'>{formatValue(couple?.personOne?.lastName)}</span>
                  </div>
                  <div className='flex items-center gap-[6px] text-[14px] text-[#202020]'>
                    <span className='font-normal'>Email:</span>
                    <span className='font-bold'>{formatValue(couple?.personOne?.email)}</span>
                  </div>
                  <div className='flex items-center gap-[6px] text-[14px] text-[#202020]'>
                    <span className='font-normal'>Phone No.:</span>
                    <span className='font-bold'>{formatValue(couple?.personOne?.phoneNumber)}</span>
                  </div>
                  <div className='flex items-center gap-[6px] text-[14px] text-[#202020]'>
                    <span className='font-normal'>Date of birth:</span>
                    <span className='font-bold'>{formatDate(couple?.personOne?.dateOfBirth)}</span>
                  </div>
                  <div className='flex items-center gap-[6px] text-[14px] text-[#202020]'>
                    <span className='font-normal'>Tribe:</span>
                    <span className='font-bold'>{formatValue(couple?.personOne?.tribe)}</span>
                  </div>

                  {/* Genotype File */}
                  {/* <div className='pt-4'>
                    <div className='bg-white border border-[#efefef] rounded-[12px] p-4'>
                      <div className='flex items-center justify-between'>
                        <div className='flex items-center gap-2'>
                          <div className='w-[36px] h-[36px]'></div>
                          <div>
                            <p className='text-[12px] font-semibold text-[#1c1c1c]'>filename.jpg</p>
                            <p className='text-[12px] font-normal text-[#ff3636]'>Upload failed</p>
                          </div>
                        </div>
                        <div className='flex items-center gap-1'>
                          <button className='w-[24px] h-[24px] flex items-center justify-center'>
                            <svg width='14' height='18' viewBox='0 0 14 18' fill='none' stroke='#6c6c6c' strokeWidth='1.5'>
                              <path d='M1 4h12M5.5 8v5M8.5 8v5M2.5 4V3a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v1M3 4v10a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V4H3z' />
                            </svg>
                          </button>
                          <button className='w-[24px] h-[24px] flex items-center justify-center'>
                            <svg width='18' height='18' viewBox='0 0 18 18' fill='none' stroke='#6c6c6c' strokeWidth='1.5'>
                              <path d='M1 6l3-3 3 3M4 3v7M17 12l-3 3-3-3M14 15V8' />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div> */}
                </div>

                {/* Progress */}
                <div className='mt-6 bg-[rgba(243,244,246,0.46)] rounded-[10px] p-6'>
                  <div className='flex items-center justify-between gap-2 mb-4'>
                    <h3 className='text-[16px] sm:text-[20px] font-bold text-black truncate'>{personOneLabel}'s Progress</h3>
                    <span className='px-3 py-1 border border-[#151515] rounded-full text-[10px] font-normal text-[#151515]'>
                      3/5 sections done
                    </span>
                  </div>
                  <div className='space-y-2'>
                    <p className='text-[12px] text-black'>
                      <span className='font-bold'>{personOneProgress}% </span>
                      <span className='font-normal'>of his assessment been done</span>
                    </p>
                    <div className='w-full h-[4px] bg-[#4e4d4d] bg-opacity-25 rounded-full'>
                      <div
                        className='h-full bg-[#FF6600] rounded-full'
                        style={{ width: `${personOneProgress}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Woman Section */}
            <div className='flex flex-col gap-4'>
              <h2 className='text-[18px] font-bold text-[#151515] leading-[28px]'>Woman</h2>
              <div className='bg-white border border-[#e8e8e8] rounded-[10px] p-5'>
                <div className='space-y-[18px]'>
                  <div className='flex items-center gap-[6px] text-[14px] text-[#202020]'>
                    <span className='font-normal'>First Name:</span>
                    <span className='font-bold'>{formatValue(couple?.personTwo?.firstName)}</span>
                  </div>
                  <div className='flex items-center gap-[6px] text-[14px] text-[#202020]'>
                    <span className='font-normal'>Last Name:</span>
                    <span className='font-bold'>{formatValue(couple?.personTwo?.lastName)}</span>
                  </div>
                  <div className='flex items-center gap-[6px] text-[14px] text-[#202020]'>
                    <span className='font-normal'>Email:</span>
                    <span className='font-bold'>{formatValue(couple?.personTwo?.email)}</span>
                  </div>
                  <div className='flex items-center gap-[6px] text-[14px] text-[#202020]'>
                    <span className='font-normal'>Phone No.:</span>
                    <span className='font-bold'>{formatValue(couple?.personTwo?.phoneNumber)}</span>
                  </div>
                  <div className='flex items-center gap-[6px] text-[14px] text-[#202020]'>
                    <span className='font-normal'>Date of birth:</span>
                    <span className='font-bold'>{formatDate(couple?.personTwo?.dateOfBirth)}</span>
                  </div>
                  <div className='flex items-center gap-[6px] text-[14px] text-[#202020]'>
                    <span className='font-normal'>Tribe:</span>
                    <span className='font-bold'>{formatValue(couple?.personTwo?.tribe)}</span>
                  </div>

                  {/* Genotype File */}
                  {/* <div className='pt-4'>
                    <div className='bg-white border border-[#efefef] rounded-[12px] p-4'>
                      <div className='flex items-center justify-between'>
                        <div className='flex items-center gap-2'>
                          <div className='w-[36px] h-[36px]'></div>
                          <div>
                            <p className='text-[12px] font-semibold text-[#1c1c1c]'>filename.jpg</p>
                            <p className='text-[12px] font-normal text-[#ff3636]'>Upload failed</p>
                          </div>
                        </div>
                        <div className='flex items-center gap-1'>
                          <button className='w-[24px] h-[24px] flex items-center justify-center'>
                            <svg width='14' height='18' viewBox='0 0 14 18' fill='none' stroke='#6c6c6c' strokeWidth='1.5'>
                              <path d='M1 4h12M5.5 8v5M8.5 8v5M2.5 4V3a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v1M3 4v10a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V4H3z' />
                            </svg>
                          </button>
                          <button className='w-[24px] h-[24px] flex items-center justify-center'>
                            <svg width='18' height='18' viewBox='0 0 18 18' fill='none' stroke='#6c6c6c' strokeWidth='1.5'>
                              <path d='M1 6l3-3 3 3M4 3v7M17 12l-3 3-3-3M14 15V8' />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div> */}
                </div>

                {/* Progress */}
                <div className='mt-6 bg-[rgba(243,244,246,0.46)] rounded-[10px] p-6'>
                  <div className='flex items-center justify-between gap-2 mb-4'>
                    <h3 className='text-[16px] sm:text-[20px] font-bold text-black truncate'>{personTwoLabel}'s Progress</h3>
                    <span className='px-3 py-1 border border-[#151515] rounded-full text-[10px] font-normal text-[#151515]'>
                      3/5 sections done
                    </span>
                  </div>
                  <div className='space-y-2'>
                    <p className='text-[12px] text-black'>
                      <span className='font-bold'>{personTwoProgress}% </span>
                      <span className='font-normal'>of his assessment been done</span>
                    </p>
                    <div className='w-full h-[4px] bg-[#4e4d4d] bg-opacity-25 rounded-full'>
                      <div
                        className='h-full bg-[#FF6600] rounded-full'
                        style={{ width: `${personTwoProgress}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

          {activeTab === 'performance' && <PerformanceTab data={couple?.performance ?? null} />}
          {activeTab === 'certification' && <CertificationApprovalTab data={couple?.certification ?? null} />}
          {activeTab === 'feedback' && <FeedbackTab data={couple?.feedback ?? null} />}
        </div>
      </div>
    </div>
  )
}
